import React from 'react';
import { Box, Link, Typography } from '@mui/material';
import { CameraAlt as CameraIcon } from '@mui/icons-material';

/**
 * Unsplash Attribution Component
 * Displays photographer credit for Unsplash images
 * Required by Unsplash API Guidelines
 * 
 * @param {string} imageUrl - The Unsplash image URL
 * @param {string} photographerName - Photographer's display name (stored in trip data)
 * @param {string} photographerUsername - Photographer's Unsplash username (stored in trip data)
 * @param {string} variant - Display variant: 'overlay' (on image) or 'below' (under image)
 * @param {object} sx - Additional styling
 */
const UnsplashAttribution = ({ imageUrl, photographerName, photographerUsername, variant = 'overlay', sx = {} }) => {
  // Check if it's an Unsplash image
  if (!imageUrl || !imageUrl.includes('images.unsplash.com')) {
    return null;
  }

  const unsplashUrl = 'https://unsplash.com/?utm_source=dejavunomad&utm_medium=referral';
  
  // Build photographer profile URL if we have the username
  const photographerUrl = photographerUsername 
    ? `https://unsplash.com/@${photographerUsername}?utm_source=dejavunomad&utm_medium=referral`
    : unsplashUrl;
  
  // Use provided photographer name or fallback to "Unsplash"
  const displayName = photographerName || 'Unsplash';

  // Overlay variant (watermark style - always visible)
  if (variant === 'overlay') {
    return (
      <Box
        sx={{
          position: 'absolute',
          bottom: 8,
          right: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.5,
          py: 0.5,
          background: 'rgba(0, 0, 0, 0.1)', // More opaque for better readability
          backdropFilter: 'blur(4px)',
          borderRadius: 2,
          transition: 'all 0.3s ease',
          pointerEvents: 'auto', // Enable click events
          zIndex: 1, // Above the photo, below chips and overflow menus
          '&:hover': {
            background: 'rgba(0, 0, 0, 0.5)', // Even darker on hover
            backdropFilter: 'blur(6px)',
            transform: 'scale(1.02)'
          },
          ...sx
        }}
      >
        <CameraIcon sx={{ fontSize: 13, color: 'white', opacity: 0.85 }} />
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.7rem', fontWeight: 400, lineHeight: 1.4 }}>
          Photo by{' '}
          <Link
            href={photographerUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()} // Prevent card click
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              textDecoration: 'none',
              borderBottom: '1px solid rgba(255, 255, 255, 0.5)',
              fontWeight: 500,
              transition: 'all 0.2s ease',
              '&:hover': {
                borderBottomColor: 'rgba(255, 255, 255, 0.9)',
                color: 'white',
                textShadow: '0 0 8px rgba(255, 255, 255, 0.5)'
              }
            }}
          >
            {displayName}
          </Link>
          {' '}on{' '}
          <Link
            href={unsplashUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              textDecoration: 'none',
              borderBottom: '1px solid rgba(255, 255, 255, 0.5)',
              fontWeight: 500,
              transition: 'all 0.2s ease',
              '&:hover': {
                borderBottomColor: 'rgba(255, 255, 255, 0.9)',
                color: 'white',
                textShadow: '0 0 8px rgba(255, 255, 255, 0.5)'
              }
            }}
          >
            Unsplash
          </Link>
        </Typography>
      </Box>
    );
  }

  // Below variant (appears under the image)
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        mt: 1,
        px: 1,
        py: 0.5,
        ...sx
      }}
    >
      <CameraIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
        Photo by{' '}
        <Link
          href={photographerUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'text.secondary', textDecoration: 'underline' }}
        >
          {photographerName}
        </Link>
        {' '}on{' '}
        <Link
          href={unsplashUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'text.secondary', textDecoration: 'underline' }}
        >
          Unsplash
        </Link>
      </Typography>
    </Box>
  );
};

export default UnsplashAttribution;

